import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { useAutoCity } from "@/hooks/useAutoCity";
import { api } from "@/lib/api";
import { CatIcon, CITIES } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, Star, MapPin, ShieldCheck, BadgeCheck, Headphones, ArrowRight,
  Briefcase, HardHat, Heart, Rocket,
} from "lucide-react";

const IMG = {
  employer: "https://static.prod-images.emergentagent.com/jobs/c65782b2-937c-4bda-be21-b495b0d54d9d/images/7b058ba5dadd4a3b3add41c3e9ac53e496b897522e5731bb743e65585617feca.jpeg",
  usta: "https://static.prod-images.emergentagent.com/jobs/c65782b2-937c-4bda-be21-b495b0d54d9d/images/1c60727325b1831f66eb9fcb977bc944e6e49a80e26ed093e1add2ce3530fa4d.jpeg",
  bannerUsta: "https://static.prod-images.emergentagent.com/jobs/c65782b2-937c-4bda-be21-b495b0d54d9d/images/6762d4daef2273b6d6e242a642b40ca40d5b0dc810284510527abee0503bda42.jpeg",
  interior: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [ustalar, setUstalar] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();
  const detectedCity = useAutoCity();

  useEffect(() => { if (detectedCity && !city) setCity(detectedCity); }, [detectedCity]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/featured-ustalar").then(({ data }) => setUstalar(data.items)).catch(() => {});
  }, []);

  const search = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city && city !== "all") params.set("city", city);
    navigate(`/ilanlar?${params.toString()}`);
  };

  return (
    <div>
      {/* ===== HERO (3 part) ===== */}
      <section className="relative">
        <div className="relative overflow-hidden border-b border-emerald-100 bg-gradient-to-b from-emerald-50 via-emerald-50/40 to-white">
          {/* edge photos */}
          <img src={IMG.employer} alt="işveren" className="hidden lg:block absolute left-0 top-0 h-full w-[20%] object-cover object-top" />
          <img src={IMG.usta} alt="usta" className="hidden lg:block absolute right-0 top-0 h-full w-[20%] object-cover object-top" />
          {/* fade edges into background */}
          <div className="hidden lg:block absolute left-[18%] top-0 h-full w-24 bg-gradient-to-r from-transparent to-emerald-50 z-[1]" />
          <div className="hidden lg:block absolute right-[18%] top-0 h-full w-24 bg-gradient-to-l from-transparent to-white z-[1]" />

          <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <div className="grid lg:grid-cols-[1fr_1.1fr_1fr] gap-5 items-center lg:px-[14%]">
              {/* Left card - İşveren */}
              <div className="bg-white rounded-2xl shadow-lg shadow-emerald-900/5 border border-slate-100 p-6 animate-fade-up order-2 lg:order-1">
                <div className="flex items-center gap-2 text-emerald-600 font-semibold"><Briefcase className="h-5 w-5" /> İşveren</div>
                <h3 className="text-xl font-extrabold text-slate-900 mt-2">İş mi yaptıracaksın?</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">İhtiyacın olan usta için ilan oluştur. Hemen teklifleri incele.</p>
                <Button onClick={() => navigate("/ilan-ver")} data-testid="hero-employer-cta" className="w-full brand-gradient text-white rounded-xl h-12 mt-5 font-semibold">
                  İş İlanı Ver <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>

              {/* Center text */}
              <div className="text-center animate-fade-up order-1 lg:order-2">
                <div className="text-5xl lg:text-6xl font-extrabold text-emerald-600 tracking-tight">ustayla</div>
                <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900 mt-4 leading-tight">Doğru Usta, Doğru İş,<br />Güvenli İletişim</h1>
                <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-sm mx-auto">
                  İster iş arayan bir usta ol, ister iş yaptırmak isteyen bir ev sahibi... Ustayla'da herkes için doğru adres var.
                </p>
              </div>

              {/* Right card - Usta */}
              <div className="bg-white rounded-2xl shadow-lg shadow-emerald-900/5 border border-slate-100 p-6 animate-fade-up order-3">
                <div className="flex items-center gap-2 text-emerald-600 font-semibold"><HardHat className="h-5 w-5" /> Usta</div>
                <h3 className="text-xl font-extrabold text-slate-900 mt-2">İş mi arıyorsun?</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">Size uygun ilanları gör, teklif ver ve çalışmaya başla.</p>
                <Button onClick={() => navigate("/ilanlar")} data-testid="hero-usta-cta" className="w-full brand-gradient text-white rounded-xl h-12 mt-5 font-semibold">
                  İş Ara <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Search bar ===== */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 relative z-10">
          <div className="bg-white rounded-full shadow-xl border border-slate-100 p-2 flex items-center gap-2 mt-6" data-testid="hero-search-card">
            <div className="flex-1 flex items-center gap-2 pl-4">
              <Search className="h-5 w-5 text-slate-400 shrink-0" />
              <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="Aradığın işi, ustayı ya da kategoriyi yaz..." className="border-0 focus-visible:ring-0 px-0 shadow-none" />
            </div>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-40 sm:w-44 rounded-full border-slate-200 bg-white" data-testid="hero-search-city-select">
                <span className="flex items-center gap-1.5 text-slate-600"><MapPin className="h-4 w-4 text-emerald-500" /><SelectValue placeholder="Tüm Türkiye" /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Türkiye</SelectItem>
                {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={search} className="brand-gradient text-white rounded-full px-7 h-11 font-semibold" data-testid="hero-search-submit-button">
              <Search className="h-4 w-4 mr-1.5" /> Ara
            </Button>
          </div>
        </div>
      </section>

      {/* ===== Categories row ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          {categories.map((c) => (
            <button key={c.slug} onClick={() => navigate(`/kategori/${c.slug}`)} data-testid={`home-category-${c.slug}`}
              className="rounded-2xl bg-emerald-50/70 hover:bg-emerald-100 border border-emerald-100/60 p-4 flex flex-col items-center gap-2 transition-colors">
              <span className="h-11 w-11 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-sm">
                <CatIcon name={c.icon} className="h-5 w-5" />
              </span>
              <span className="text-[11px] sm:text-xs font-semibold text-slate-700 text-center leading-tight">{c.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ===== Featured ustalar + side banners ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="grid lg:grid-cols-[1fr_290px] gap-6">
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Öne Çıkan Ustalar</h2>
              <button onClick={() => navigate("/ustalar")} className="text-sm font-semibold text-emerald-600 flex items-center gap-1" data-testid="home-all-ustalar">
                Tüm Ustalar <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
              {ustalar.map((u) => (
                <div key={u.user_id} onClick={() => navigate(`/usta/${u.user_id}`)} data-testid={`home-usta-${u.user_id}`}
                  className={`bg-white rounded-2xl overflow-hidden hover-lift cursor-pointer ${u.boosted ? "border-2 border-emerald-500 shadow-md shadow-emerald-500/10" : "border border-slate-200/80"}`}>
                  <div className="h-28 bg-emerald-50 relative flex items-center justify-center">
                    {u.avatar ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" /> : <span className="text-2xl font-extrabold text-emerald-300">{(u.name || "U")[0]}</span>}
                    {u.boosted && <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold text-white brand-gradient px-2 py-0.5 rounded-full"><Rocket className="h-2.5 w-2.5" /> Öne Çıkan</span>}
                    <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center text-slate-400 hover:text-emerald-500" onClick={(e) => { e.stopPropagation(); toggle("usta", u.user_id); }} data-testid={`home-usta-fav-${u.user_id}`}><Heart className={`h-3.5 w-3.5 ${isFav("usta", u.user_id) ? "fill-emerald-500 text-emerald-500" : ""}`} /></span>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-slate-900 text-sm truncate">{u.name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.specialty}</div>
                    <div className="flex items-center gap-1 mt-1.5 text-xs">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      <span className="font-bold text-slate-800">{u.rating}</span>
                      <span className="text-slate-400">({u.review_count})</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-400"><MapPin className="h-3 w-3" /> {(u.service_areas || [])[0]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Side banners */}
          <div className="flex flex-col gap-4">
            <div className="relative rounded-2xl bg-emerald-50 border border-emerald-100 p-5 overflow-hidden">
              <div className="relative z-10 max-w-[62%]">
                <h3 className="text-lg font-extrabold text-slate-900 leading-tight">Ustalara özel fırsatlar ve avantajlar!</h3>
                <p className="text-xs text-slate-500 mt-2">Hemen üye ol, siz de kazanmaya başla.</p>
                <Button onClick={() => navigate("/ilanlar")} className="brand-gradient text-white rounded-xl mt-4 h-10 text-sm font-semibold" data-testid="banner-usta-signup">
                  Ücretsiz Kayıt Ol
                </Button>
              </div>
              <img src={IMG.bannerUsta} alt="" className="absolute right-0 bottom-0 h-full w-[42%] object-cover object-left" />
            </div>

            <div className="relative rounded-2xl overflow-hidden min-h-[180px] flex items-end p-5">
              <img src={IMG.interior} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/85 via-emerald-950/40 to-transparent" />
              <div className="relative z-10 text-white">
                <h3 className="text-lg font-extrabold leading-tight">Hayalindeki evi birlikte tamir edelim.</h3>
                <Button onClick={() => navigate("/ilanlar")} variant="secondary" className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl mt-3 h-10 text-sm font-semibold" data-testid="banner-browse-jobs">
                  İlanlara Göz At <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Trust strip ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-2xl bg-emerald-50/70 border border-emerald-100/60 p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Güvenli İletişim", d: "Ödeme korumalı sistem" },
            { icon: BadgeCheck, t: "Doğrulanmış Ustalar", d: "Kimlik ve referans kontrolü" },
            { icon: Star, t: "Yorum & Puanlama", d: "Gerçek kullanıcı yorumları" },
            { icon: Headphones, t: "7/24 Destek", d: "Her zaman yanınızda" },
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-full bg-white flex items-center justify-center text-emerald-600 shadow-sm shrink-0"><x.icon className="h-5 w-5" /></span>
              <div>
                <div className="font-bold text-slate-900 text-sm">{x.t}</div>
                <div className="text-xs text-slate-500">{x.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
